/**
 * Web component for cart drawer product recommendations carousel
 * Handles carousel navigation and scrolling behavior
 */
class CartDrawerRecommendationsComponent extends HTMLElement {
  /**
   * Scroll amount for carousel navigation
   * Calculated dynamically based on card width for better performance
   * @type {number|null}
   */
  #scrollAmount = null;

  /**
   * Gets the scroll amount, calculating it once and caching
   * @returns {number}
   */
  #getScrollAmount() {
    if (this.#scrollAmount === null) {
      const { carousel } = this.#refs;
      if (carousel) {
        // Calculate based on first card width + gap
        const firstCard = carousel.querySelector('.cart-drawer-rec-card');
        if (firstCard) {
          this.#scrollAmount = firstCard.offsetWidth + 12; // 12px gap
        } else {
          this.#scrollAmount = 160; // Fallback
        }
      } else {
        this.#scrollAmount = 160; // Fallback
      }
    }
    return this.#scrollAmount;
  }

  /**
   * References to DOM elements
   * @type {Object}
   */
  #refs = {};

  /**
   * AbortController for cleaning up event listeners
   * @type {AbortController|null}
   */
  #abortController = null;

  /**
   * ResizeObserver for tracking carousel size changes
   * @type {ResizeObserver|null}
   */
  #resizeObserver = null;

  /**
   * Cleanup function for RAF and timeouts
   * @type {Function|null}
   */
  #cleanupRAF = null;

  /**
   * Loads the external CSS file from data-css attribute
   */
  #loadCSS() {
    const cssUrl = this.dataset.css;
    if (!cssUrl || document.querySelector(`link[href="${cssUrl}"]`)) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    document.head.appendChild(link);
  }

  connectedCallback() {
    // Use Intersection Observer to defer initialization until component is visible
    // This improves initial page load performance
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          this.#initialize();
        }
      }, { rootMargin: '50px' });
      observer.observe(this);
    } else {
      // Fallback for browsers without IntersectionObserver
      this.#initialize();
    }
  }

  /**
   * Initializes the component functionality
   */
  #initialize() {
    this.#loadCSS();
    // Use event delegation on the component itself to handle button clicks
    // This avoids issues with duplicate listeners and works with dynamically added content
    this.#abortController = new AbortController();

    this.addEventListener('click', (e) => {
      const button = e.target.closest('[data-slideshow-control]');
      if (button && this.contains(button) && !button.disabled) {
        e.preventDefault();
        this.#handleNavigation(button);
      }
    }, { signal: this.#abortController.signal });

    // Add keyboard navigation support
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const carousel = this.#refs.carousel;
        if (carousel && document.activeElement === carousel) {
          e.preventDefault();
          const button = this.querySelector(
            `[data-slideshow-control="${e.key === 'ArrowLeft' ? 'prev' : 'next'}"]`
          );
          if (button && !button.disabled) {
            this.#handleNavigation(button);
          }
        }
      }
    }, { signal: this.#abortController.signal });

    // Update refs and set up scroll tracking
    this.#updateRefs();
    this.#setupScrollTracking();
  }

  disconnectedCallback() {
    if (this.#abortController) {
      this.#abortController.abort();
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
    if (this.#cleanupRAF) {
      this.#cleanupRAF();
    }
  }

  /**
   * Updates references to DOM elements with ref attributes
   */
  #updateRefs() {
    const carousel = this.querySelector('[ref="cartDrawerRecommendationsCarousel"]');
    const prevButton = this.querySelector('[data-slideshow-control="prev"]');
    const nextButton = this.querySelector('[data-slideshow-control="next"]');

    this.#refs = {
      carousel,
      prevButton,
      nextButton
    };
  }

  /**
   * Sets up scroll tracking to enable/disable navigation buttons
   * Uses requestAnimationFrame for performance optimization
   */
  #setupScrollTracking() {
    const { carousel, prevButton, nextButton } = this.#refs;
    if (!carousel || (!prevButton && !nextButton)) return;

    let rafId = null;
    let isTracking = false;

    const updateButtonStates = () => {
      const { scrollLeft, scrollWidth, clientWidth } = carousel;
      const isAtStart = scrollLeft <= 0;
      const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1;

      if (prevButton) {
        prevButton.disabled = isAtStart;
        prevButton.setAttribute('aria-disabled', String(isAtStart));
      }
      if (nextButton) {
        nextButton.disabled = isAtEnd;
        nextButton.setAttribute('aria-disabled', String(isAtEnd));
      }

      isTracking = false;
      rafId = null;
    };

    const scheduleUpdate = () => {
      if (!isTracking) {
        isTracking = true;
        rafId = requestAnimationFrame(updateButtonStates);
      }
    };

    // Initial state update
    updateButtonStates();

    // Update on scroll with RAF throttling
    carousel.addEventListener('scroll', scheduleUpdate, { signal: this.#abortController.signal, passive: true });

    // Update on resize (carousel width might change) with debouncing
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateButtonStates, 150);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(carousel);

    // Store observer for cleanup
    this.#resizeObserver = resizeObserver;

    // Cleanup RAF on disconnect
    this.#cleanupRAF = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      clearTimeout(resizeTimeout);
    };
  }

  /**
   * Handles carousel navigation based on button click
   * @param {HTMLElement} button - The navigation button that was clicked
   */
  #handleNavigation(button) {
    const { carousel } = this.#refs;
    if (!carousel) return;

    const control = button.getAttribute('data-slideshow-control');
    const ref = button.getAttribute('data-slideshow-ref');

    // Find the target carousel container
    let targetCarousel = carousel;
    if (ref) {
      // Try to find carousel by ref attribute within this component
      const refCarousel = this.querySelector(`[ref="${ref}"]`);
      if (refCarousel) {
        targetCarousel = refCarousel;
      }
    }

    if (!targetCarousel) return;

    const scrollAmount = this.#getScrollAmount();
    if (control === 'prev') {
      targetCarousel.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else if (control === 'next') {
      targetCarousel.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('cart-drawer-recommendations-component')) {
  customElements.define('cart-drawer-recommendations-component', CartDrawerRecommendationsComponent);
}
